/**
 * SoleadLab 2.0 - Google Drive Import Wizard
 *
 * 4-step modal:
 * 1. Paste folder URL → scan
 * 2. Review categorized files (with PDF classification)
 * 3. Import progress
 * 4. Summary
 */

import { useState, useCallback, useRef } from 'react';
import { useUIStore, useProjectStore, useMapStore, useMonitoringStore } from '../../stores';
import { listDriveFolder, uploadAsset } from '../../services/assetApi';
import type { DriveFileImportResult, DriveSrtResult } from '../../services/assetApi';
import { apiFetchRaw } from '../../services/apiFetch';
import { requestTileGeneration } from '../../services/tileApi';
import {
  categorizeFiles,
  buildImportSummary,
  matchPhotoSets,
  matchFlightPaths,
  collectExistingMedia,
  markAlreadyImported,
  importFiles,
  filesToProcess,
  formatBytes,
  driveThumbnailUrl,
  driveDirectUrl,
  primaryFlightSrt,
  assignPhotosToFlights,
  synthesizeFlightRoute,
  djiCaptureMs,
} from '../../utils/driveImport';
import type {
  CategorizedFile,
  PdfPurpose,
  ImportProgress,
  PhotoSet,
  FlightPathSet,
  ImportSummary,
  SynthPhotoPoint,
} from '../../utils/driveImport';
import { parseSrt } from '../../utils/srt';
import { rectGeomFromCenter, defaultSizeForType } from '../../utils/geometry';
import { captureDay } from '../../utils/captureDate';
import './DriveImportModal.css';

type WizardStep = 'url' | 'review' | 'importing' | 'summary';

/** Color for photo-synthesized (no-SRT) approximate routes — deliberately
 *  distinct from the orange (#ff8c00) used for real SRT flight paths so the two
 *  are visually distinguishable on the map. */
const APPROX_FLIGHT_COLOR = '#22d3ee';

export default function DriveImportModal() {
  const closeModal = useUIStore((s) => s.closeModal);
  const siteId = useProjectStore((s) => s.meta.siteId);
  const addIndication = useProjectStore((s) => s.addIndication);
  const addFlightPath = useProjectStore((s) => s.addFlightPath);

  // ── Wizard state ──
  const [step, setStep] = useState<WizardStep>('url');
  const [folderUrl, setFolderUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [scanError, setScanError] = useState('');
  const [files, setFiles] = useState<CategorizedFile[]>([]);
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [truncationReason, setTruncationReason] = useState<'files' | 'depth' | 'folders' | null>(null);
  /** Files flagged at scan time as already imported into this project (P1 dedup). */
  const [alreadyFlaggedCount, setAlreadyFlaggedCount] = useState(0);
  const [progress, setProgress] = useState<ImportProgress | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // ── Step 1: Scan Folder ──
  const handleScan = useCallback(async () => {
    if (!siteId || !folderUrl.trim()) return;
    setScanning(true);
    setScanError('');
    setTruncationReason(null);

    try {
      const result = await listDriveFolder(siteId, folderUrl.trim());
      const categorized = categorizeFiles(result.files);
      // P1 dedup: flag files whose Drive ID (or filename, for orthos/PDFs) is
      // already in the project so a re-scan of the same folder doesn't double
      // everything. Flagged files default to excluded; user can override.
      const projectData = useProjectStore.getState().data;
      const existing = collectExistingMedia({
        indications: projectData.indications,
        flightPaths: projectData.flightPaths,
        orthophotos: projectData.orthophotos,
        overlays: projectData.overlays,
        slds: useMonitoringStore.getState().sldDiagrams,
      });
      const { files: marked, flaggedCount } = markAlreadyImported(categorized, existing);
      setFiles(marked);
      setSummary(buildImportSummary(marked));
      setAlreadyFlaggedCount(flaggedCount);
      setTruncationReason(result.truncated ? result.truncationReason ?? 'files' : null);
      setStep('review');
    } catch (err) {
      setScanError(err instanceof Error ? err.message : 'Failed to scan folder');
    } finally {
      setScanning(false);
    }
  }, [siteId, folderUrl]);

  // ── Step 2: Toggle file inclusion ──
  const toggleFile = useCallback((idx: number) => {
    setFiles((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], included: !next[idx].included };
      return next;
    });
  }, []);

  // Toggle a whole photo set at once (thermal/visual/zoom follow together) so
  // unchecking a set can't strand still-included siblings that would then
  // import on their own.
  const toggleFileSet = useCallback((idxs: number[]) => {
    setFiles((prev) => {
      const first = idxs.find((i) => i >= 0 && i < prev.length);
      if (first === undefined) return prev;
      const target = !prev[first].included;
      const next = [...prev];
      for (const i of idxs) {
        if (i >= 0 && i < next.length) next[i] = { ...next[i], included: target };
      }
      return next;
    });
  }, []);

  const setPdfPurpose = useCallback((idx: number, purpose: PdfPurpose) => {
    setFiles((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], pdfPurpose: purpose };
      return next;
    });
  }, []);

  // Bulk override for the P1 dedup skip: re-include everything flagged as
  // already imported (the alreadyImported badge stays on the rows).
  const includeAlreadyImported = useCallback(() => {
    setFiles((prev) =>
      prev.map((f) =>
        f.alreadyImported && f.category !== 'unknown' ? { ...f, included: true } : f
      )
    );
  }, []);

  // Recompute summary when files change
  const currentSummary = files.length > 0 ? buildImportSummary(files) : summary;

  // Review rows are matched over ALL files (not just included ones) so
  // unchecking a row doesn't remove it from the list — the checkbox just
  // clears. `currentSummary` (included-only) still drives the summary bar
  // and byte totals.
  const reviewPhotoSets = matchPhotoSets(files);
  const reviewFlightPaths = matchFlightPaths(files);
  const reviewGeotiffs = files.filter((f) => f.category === 'geotiff');
  const reviewPdfs = files.filter((f) => f.category === 'pdf');
  const reviewUnknowns = files.filter((f) => f.category === 'unknown');

  // ── Step 3: Start Import ──
  const handleImport = useCallback(async () => {
    if (!siteId) return;
    setStep('importing');

    const abort = new AbortController();
    abortRef.current = abort;

    const finalProgress = await importFiles(siteId, files, setProgress, abort.signal);

    // ── Create project entries from import results ──
    const photoSets = matchPhotoSets(files.filter((f) => f.included));
    const flightSets = matchFlightPaths(files.filter((f) => f.included));

    // Collect the EXIF GPS of every imported (thermal) photo so a flight with no
    // SRT telemetry can have an approximate route synthesized from its photos.
    const photoGpsPoints: SynthPhotoPoint[] = [];

    // Process photo sets → Indications. Anchor priority thermal ?? visual ?? zoom
    // (same as local upload) so visual-only / wide-only / zoom-only sets — e.g.
    // a consumer `_D` photo with no thermal sibling — are imported, not dropped.
    for (const set of photoSets) {
      const anchor = set.thermal ?? set.visual ?? set.zoom;
      if (!anchor) continue;
      const sz = defaultSizeForType('Photo');

      if (set.thermal) {
        // ── Thermal-anchored set (unchanged): the R2-uploaded thermal supplies
        //    the primary image + GPS; visual/zoom ride along as Drive thumbnails.
        const thermalProgress = finalProgress.files.find(
          (fp) => fp.file.driveFile.id === set.thermal!.driveFile.id && fp.status === 'done'
        );
        if (!thermalProgress?.result || 'content' in thermalProgress.result) continue;

        const result = thermalProgress.result as DriveFileImportResult;
        const exif = result.exif;
        if (!exif) continue; // No GPS — skip

        // Record this photo's GPS for no-SRT flight-route synthesis (below).
        photoGpsPoints.push({
          lng: exif.lng,
          lat: exif.lat,
          alt: exif.altitude,
          captureDate: exif.captureDate,
          name: set.thermal!.driveFile.name,
          pathPrefix: set.pathPrefix,
        });

        // Build visual/zoom Drive thumbnail URLs
        const visualImage = set.visual
          ? driveThumbnailUrl(set.visual.driveFile.id, 2000)
          : undefined;
        const zoomImage = set.zoom
          ? driveThumbnailUrl(set.zoom.driveFile.id, 2000)
          : undefined;
        const visualDriveId = set.visual?.driveFile.id;
        const zoomDriveId = set.zoom?.driveFile.id;

        // Create a rectangular polygon centered at GPS coords (same as processGeoPhotos)
        const geometry = rectGeomFromCenter(exif.lng, exif.lat, sz.w, sz.h);

        // Observation time = the photo's EXIF timestamp (fall back to the DJI
        // filename time, then import time). captureDate is just the scan DAY.
        const djiMs = djiCaptureMs(set.thermal!.driveFile.name);
        const observedIso = exif.captureDate
          || (djiMs != null ? new Date(djiMs).toISOString() : new Date().toISOString());

        addIndication({
          id: crypto.randomUUID(),
          type: 'Photo',
          geometry,
          properties: {
            image: result.url,
            visualImage,
            zoomImage,
            visualDriveId,
            zoomDriveId,
            driveFileId: set.thermal!.driveFile.id,
            notes: `GPS: ${exif.lat.toFixed(6)}, ${exif.lng.toFixed(6)}${exif.altitude ? ` Alt: ${exif.altitude}m` : ''}${exif.captureDate ? `\nDate: ${exif.captureDate}` : ''}`,
            captureDate: captureDay(observedIso) || undefined,
            createdAt: observedIso,
            updatedAt: new Date().toISOString(),
          },
        });
        continue;
      }

      // ── Visual-only / zoom-only set (V-01D) ──
      // No thermal in this set, so the anchor (visual or zoom) got an
      // EXIF-only PEEK from the backend (see filesToProcess) — the photo
      // itself stays hosted on Drive, nothing in R2. Place at the photo's
      // EXIF GPS when present; fall back to map center when the peek failed
      // or the file has no GPS — never skip. Sibling toggle URLs are set only
      // for a file OTHER than the anchor, so the primary is never duplicated
      // as a redundant toggle.
      const anchorProgress = finalProgress.files.find(
        (fp) => fp.file.driveFile.id === anchor.driveFile.id && fp.status === 'done'
      );
      const anchorResult = anchorProgress?.result;
      const anchorExif =
        anchorResult && 'exif' in anchorResult ? anchorResult.exif : undefined;

      let lng: number, lat: number;
      if (anchorExif) {
        lng = anchorExif.lng;
        lat = anchorExif.lat;
        // Feed no-SRT flight-route synthesis, same as thermal-anchored photos.
        photoGpsPoints.push({
          lng: anchorExif.lng,
          lat: anchorExif.lat,
          alt: anchorExif.altitude,
          captureDate: anchorExif.captureDate,
          name: anchor.driveFile.name,
          pathPrefix: set.pathPrefix,
        });
      } else {
        const map = useMapStore.getState().map;
        const center = map ? map.getCenter() : { lng: useMapStore.getState().center[0], lat: useMapStore.getState().center[1] };
        lng = center.lng + (Math.random() - 0.5) * 0.0002;
        lat = center.lat + (Math.random() - 0.5) * 0.0002;
      }
      const geometry = rectGeomFromCenter(lng, lat, sz.w, sz.h);

      const visualImage = set.visual && set.visual !== anchor
        ? driveThumbnailUrl(set.visual.driveFile.id, 2000) : undefined;
      const zoomImage = set.zoom && set.zoom !== anchor
        ? driveThumbnailUrl(set.zoom.driveFile.id, 2000) : undefined;
      const visualDriveId = set.visual && set.visual !== anchor ? set.visual.driveFile.id : undefined;
      const zoomDriveId = set.zoom && set.zoom !== anchor ? set.zoom.driveFile.id : undefined;

      // Observation time: EXIF timestamp → DJI filename time → import time.
      const djiMsV = djiCaptureMs(anchor.driveFile.name);
      const observedIsoV = anchorExif?.captureDate
        || (djiMsV != null ? new Date(djiMsV).toISOString() : new Date().toISOString());
      const hasRealTimeV = !!anchorExif?.captureDate || djiMsV != null;

      addIndication({
        id: crypto.randomUUID(),
        type: 'Photo',
        geometry,
        properties: {
          image: driveThumbnailUrl(anchor.driveFile.id, 2000),
          imageRole: 'visual' as const,
          visualImage,
          zoomImage,
          visualDriveId,
          zoomDriveId,
          driveFileId: anchor.driveFile.id,
          notes: anchorExif
            ? `GPS: ${anchorExif.lat.toFixed(6)}, ${anchorExif.lng.toFixed(6)}${anchorExif.altitude ? ` Alt: ${anchorExif.altitude}m` : ''}${anchorExif.captureDate ? `\nDate: ${anchorExif.captureDate}` : ''}`
            : `Source: Google Drive — ${anchor.driveFile.name}\nGPS: not available (placed at map center)`,
          captureDate: hasRealTimeV ? (captureDay(observedIsoV) || undefined) : undefined,
          createdAt: observedIsoV,
          updatedAt: new Date().toISOString(),
        },
      });
    }

    // Segment the imported photos into flight groups by DJI sequence number so a
    // flight with no SRT can borrow the GPS of the photos captured during it.
    const photosByFlight = assignPhotosToFlights(flightSets, photoGpsPoints);

    // Process flight paths → FlightPath entries (thermal/visual/zoom/screen merged)
    for (const set of flightSets) {
      // Drive IDs of every source file in the set — dedup keys for re-imports.
      const driveFileIds = [
        set.thermalSrt, set.visualSrt, set.zoomSrt, set.screenSrt,
        set.thermalVideo, set.visualVideo, set.zoomVideo, set.screenVideo,
      ].filter((f): f is NonNullable<typeof f> => !!f).map((f) => f.driveFile.id);
      // Use the primary SRT for coordinates (thermal → visual → zoom → screen)
      const primarySrt = primaryFlightSrt(set);
      if (!primarySrt) {
        // ── No SRT telemetry: synthesize an approximate route (cyan) from the
        //    photos captured during this flight and attach its videos. If there
        //    aren't ≥2 geotagged photos to form a line, fall through to the
        //    current behavior (skip) so nothing about the existing flow — or the
        //    real-SRT path below — changes. ──
        const points = photosByFlight.get(`${set.pathPrefix}${set.baseName}`) ?? [];
        const flightMs = djiCaptureMs(set.baseName);
        const synth = synthesizeFlightRoute(points, flightMs);
        if (!synth) continue;
        const flightDay = flightMs != null ? (captureDay(new Date(flightMs).toISOString()) || undefined) : undefined;

        const thermalVideoUrl = set.thermalVideo ? driveDirectUrl(set.thermalVideo.driveFile.id) : undefined;
        const visualVideoUrl = set.visualVideo ? driveDirectUrl(set.visualVideo.driveFile.id) : undefined;
        const zoomVideoUrl = set.zoomVideo ? driveDirectUrl(set.zoomVideo.driveFile.id) : undefined;
        const screenVideoUrl = set.screenVideo ? driveDirectUrl(set.screenVideo.driveFile.id) : undefined;

        addFlightPath({
          id: crypto.randomUUID(),
          name: `${set.pathPrefix}${set.baseName}`,
          coordinates: synth.coordinates,
          timestamps: synth.timestamps,
          visible: true,
          color: APPROX_FLIGHT_COLOR,
          isApproximate: true,
          captureDate: flightDay,
          thermalVideoUrl,
          visualVideoUrl,
          zoomVideoUrl,
          screenVideoUrl,
          driveFileIds,
          flightStats: synth.flightStats,
        });
        continue;
      }
      const srtProgress = finalProgress.files.find(
        (fp) => fp.file.driveFile.id === primarySrt.driveFile.id && fp.status === 'done'
      );
      if (!srtProgress?.result || !('content' in srtProgress.result)) continue;

      const srtResult = srtProgress.result as DriveSrtResult;
      const parsed = parseSrt(srtResult.content, srtResult.fileName || set.baseName);
      if (!parsed || parsed.coordinates.length === 0) continue;
      const srtFlightMs = djiCaptureMs(set.baseName);
      const srtFlightDay = srtFlightMs != null ? (captureDay(new Date(srtFlightMs).toISOString()) || undefined) : undefined;

      // Build video URLs per role from Drive (each role optional)
      const thermalVideoUrl = set.thermalVideo ? driveDirectUrl(set.thermalVideo.driveFile.id) : undefined;
      const visualVideoUrl = set.visualVideo ? driveDirectUrl(set.visualVideo.driveFile.id) : undefined;
      const zoomVideoUrl = set.zoomVideo ? driveDirectUrl(set.zoomVideo.driveFile.id) : undefined;
      const screenVideoUrl = set.screenVideo ? driveDirectUrl(set.screenVideo.driveFile.id) : undefined;

      addFlightPath({
        id: crypto.randomUUID(),
        name: `${set.pathPrefix}${set.baseName}`,
        coordinates: parsed.coordinates,
        timestamps: parsed.timestamps,
        visible: true,
        color: '#ff8c00',
        captureDate: srtFlightDay,
        thermalVideoUrl,
        visualVideoUrl,
        zoomVideoUrl,
        screenVideoUrl,
        driveFileIds,
        flightStats: {
          frameCount: parsed.coordinates.length,
        },
      });
    }

    // ── Process orthophotos (GeoTIFF) → parse bounds, render PNG, upload, register ──
    const includedGeo = files.filter((f) => f.included && f.category === 'geotiff');
    for (const geoFile of includedGeo) {
      const geoProgress = finalProgress.files.find(
        (fp) => fp.file.driveFile.id === geoFile.driveFile.id && fp.status === 'done'
      );
      if (!geoProgress?.result || 'content' in geoProgress.result) continue;
      const rawResult = geoProgress.result as DriveFileImportResult;

      try {
        // Fetch raw GeoTIFF from R2 and parse with geotiff library
        const response = await apiFetchRaw(rawResult.url);
        const arrayBuffer = await response.arrayBuffer();
        const { fromArrayBuffer } = await import('geotiff');
        const tiff = await fromArrayBuffer(arrayBuffer);
        const image = await tiff.getImage();
        const bbox = image.getBoundingBox();
        if (!bbox || bbox.length < 4) continue;
        const [minX, minY, maxX, maxY] = bbox;
        const origWidth = image.getWidth();
        const origHeight = image.getHeight();

        // 4 corners: TL, TR, BR, BL — in source CRS initially
        let corners: [[number, number], [number, number], [number, number], [number, number]] = [
          [minX, maxY], [maxX, maxY], [maxX, minY], [minX, minY],
        ];

        // Reproject all 4 corners from projected CRS to WGS84 if needed
        const isProjected = Math.abs(minX) > 180 || Math.abs(maxX) > 180 || Math.abs(minY) > 90 || Math.abs(maxY) > 90;
        if (isProjected) {
          const geoKeys = image.getGeoKeys();
          const epsgCode = geoKeys.ProjectedCSTypeGeoKey || geoKeys.GeographicTypeGeoKey;
          if (epsgCode && epsgCode !== 32767) {
            const proj4 = (await import('proj4')).default;
            const resp = await fetch(`https://epsg.io/${epsgCode}.proj4`);
            if (resp.ok) {
              let projStr = (await resp.text()).trim();
              // Some CRS definitions from epsg.io lack +towgs84 datum shift parameters
              const KNOWN_TOWGS84: Record<number, string> = {
                2039: '-48,55,52,0,0,0,0',  // Israel 1993 / Israeli TM Grid
                6984: '-48,55,52,0,0,0,0',  // Israeli Grid 05/12
                6991: '-48,55,52,0,0,0,0',  // Israeli Grid 05/12 Intermediate CRS
              };
              if (!projStr.includes('+towgs84') && KNOWN_TOWGS84[epsgCode]) {
                projStr += ` +towgs84=${KNOWN_TOWGS84[epsgCode]}`;
              }
              proj4.defs(`EPSG:${epsgCode}`, projStr);
              const fwd = proj4(`EPSG:${epsgCode}`, 'EPSG:4326');
              corners = corners.map((c) => fwd.forward(c) as [number, number]) as typeof corners;
            }
          }
        }

        // Render raster to canvas (downsampled if needed)
        const MAX_DIM = 8192;
        let renderWidth = origWidth, renderHeight = origHeight;
        if (origWidth > MAX_DIM || origHeight > MAX_DIM) {
          const scale = Math.min(MAX_DIM / origWidth, MAX_DIM / origHeight);
          renderWidth = Math.round(origWidth * scale);
          renderHeight = Math.round(origHeight * scale);
        }
        const rasters = await image.readRasters({ width: renderWidth, height: renderHeight });
        const canvas = document.createElement('canvas');
        canvas.width = renderWidth;
        canvas.height = renderHeight;
        const ctx = canvas.getContext('2d')!;
        const imageData = ctx.createImageData(renderWidth, renderHeight);
        const px = imageData.data;
        const numBands = rasters.length;
        const totalPixels = renderWidth * renderHeight;
        if (numBands >= 3) {
          for (let i = 0; i < totalPixels; i++) {
            px[i * 4] = (rasters[0] as unknown as Uint8Array)[i];
            px[i * 4 + 1] = (rasters[1] as unknown as Uint8Array)[i];
            px[i * 4 + 2] = (rasters[2] as unknown as Uint8Array)[i];
            px[i * 4 + 3] = numBands >= 4 ? (rasters[3] as unknown as Uint8Array)[i] : 255;
          }
        } else if (numBands === 1) {
          for (let i = 0; i < totalPixels; i++) {
            const val = (rasters[0] as unknown as Uint8Array)[i];
            px[i * 4] = val; px[i * 4 + 1] = val; px[i * 4 + 2] = val; px[i * 4 + 3] = 255;
          }
        }
        ctx.putImageData(imageData, 0, 0);

        // Remove background (flood-fill from edges: black/white → transparent)
        {
          const bgData = ctx.getImageData(0, 0, renderWidth, renderHeight);
          const bgPx = bgData.data;
          const w = renderWidth, h = renderHeight;
          const isBg = (idx: number) => {
            const r = bgPx[idx], g = bgPx[idx + 1], b = bgPx[idx + 2];
            return (r <= 15 && g <= 15 && b <= 15) || (r >= 240 && g >= 240 && b >= 240);
          };
          const visited = new Uint8Array(w * h);
          const queue: number[] = [];
          for (let x = 0; x < w; x++) {
            if (isBg(x * 4)) { queue.push(x); visited[x] = 1; }
            const bIdx = (h - 1) * w + x;
            if (isBg(bIdx * 4)) { queue.push(bIdx); visited[bIdx] = 1; }
          }
          for (let y = 1; y < h - 1; y++) {
            const lIdx = y * w;
            if (isBg(lIdx * 4)) { queue.push(lIdx); visited[lIdx] = 1; }
            const rIdx = y * w + (w - 1);
            if (isBg(rIdx * 4)) { queue.push(rIdx); visited[rIdx] = 1; }
          }
          let head = 0;
          while (head < queue.length) {
            const pixelIdx = queue[head++];
            const x = pixelIdx % w;
            const y = (pixelIdx - x) / w;
            bgPx[pixelIdx * 4 + 3] = 0;
            const neighbors = [y > 0 ? pixelIdx - w : -1, y < h - 1 ? pixelIdx + w : -1, x > 0 ? pixelIdx - 1 : -1, x < w - 1 ? pixelIdx + 1 : -1];
            for (const nIdx of neighbors) {
              if (nIdx >= 0 && !visited[nIdx] && isBg(nIdx * 4)) { visited[nIdx] = 1; queue.push(nIdx); }
            }
          }
          ctx.putImageData(bgData, 0, 0);
        }

        // Render rendered PNG to a blob for immediate preview. We don't upload
        // to R2 here — Mapbox image sources fetch via Image() with
        // crossOrigin='anonymous', which doesn't send the auth cookie, so a
        // /api/v2/assets/... URL would 401. Use a blob: URL for the in-session
        // preview; projectSync uploads the blob on save, then tile generation
        // (kicked off below) produces the reload-safe raster source.
        const pngBlob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
        if (!pngBlob) continue;

        const lngs = corners.map((c) => c[0]);
        const lats = corners.map((c) => c[1]);
        const bMinLng = Math.min(...lngs), bMaxLng = Math.max(...lngs);
        const bMinLat = Math.min(...lats), bMaxLat = Math.max(...lats);

        const orthoId = `ortho_drive_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        useProjectStore.getState().addOrthophoto({
          id: orthoId,
          name: geoFile.driveFile.name.replace(/\.[^.]+$/, ''),
          url: URL.createObjectURL(pngBlob),
          coordinates: corners,
          bounds: { minLng: bMinLng, minLat: bMinLat, maxLng: bMaxLng, maxLat: bMaxLat },
          opacity: 100,
          visible: true,
          isManual: false,
          fileName: geoFile.driveFile.name,
          fileSize: rawResult.size,
          width: origWidth,
          height: origHeight,
          bgRemoved: true,
          sourceR2Key: rawResult.r2Key,
        });

        // Fly to orthophoto location
        const map = useMapStore.getState().map;
        if (map) map.fitBounds([[bMinLng, bMinLat], [bMaxLng, bMaxLat]], { padding: 60, duration: 1500 });

        // Trigger tile generation directly. Local-file uploads register the
        // source in IndexedDB and projectSync triggers tile-gen on save; Drive
        // imports skip that path (source already in R2 from handleImportDriveFile),
        // so we kick off tile-gen here using the existing sourceR2Key.
        try {
          const corners2D = corners.map((c) => [c[0], c[1]]);
          const tileResult = await requestTileGeneration(
            siteId,
            'orthophoto',
            orthoId,
            rawResult.r2Key,
            { minLng: bMinLng, minLat: bMinLat, maxLng: bMaxLng, maxLat: bMaxLat },
            corners2D,
            origWidth,
            origHeight,
            true,
          );
          useProjectStore.getState().updateOrthophoto(orthoId, {
            tilesetId: tileResult.tilesetId,
            tileStatus: 'generating',
          });
        } catch (tileErr) {
          console.error('GeoTIFF tile generation failed:', tileErr);
        }
      } catch (err) {
        console.error('GeoTIFF post-processing error:', err);
      }
    }

    // ── Process PDFs → render to PNG, upload, register ──
    const includedPdfs = files.filter((f) => f.included && f.category === 'pdf');
    for (const pdfFile of includedPdfs) {
      const pdfProgress = finalProgress.files.find(
        (fp) => fp.file.driveFile.id === pdfFile.driveFile.id && fp.status === 'done'
      );
      if (!pdfProgress?.result || 'content' in pdfProgress.result) continue;
      const rawResult = pdfProgress.result as DriveFileImportResult;

      try {
        // Fetch raw PDF from R2 and render page 1 with pdf.js
        const response = await apiFetchRaw(rawResult.url);
        const arrayBuffer = await response.arrayBuffer();
        const pdfjsLib = await import('pdfjs-dist/legacy/build/pdf.mjs');
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/legacy/build/pdf.worker.min.mjs', import.meta.url).toString();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const page = await pdf.getPage(1);
        const scale = 3;
        const viewport = page.getViewport({ scale });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext('2d')!;
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        await page.render({ canvasContext: ctx, viewport, canvas } as any).promise;

        // Make white pixels transparent (same as overlay flow)
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const px = imageData.data;
        for (let i = 0; i < px.length; i += 4) {
          if (px[i] > 250 && px[i + 1] > 250 && px[i + 2] > 250) px[i + 3] = 0;
        }
        ctx.putImageData(imageData, 0, 0);

        const pngBlob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
        if (!pngBlob) continue;

        if (pdfFile.pdfPurpose === 'sld') {
          // SLD is rendered via plain <img> in the SLD panel, which sends the
          // sl_refresh cookie cross-site under SameSite=Lax — so the R2 asset
          // URL works fine and survives reload. Upload to R2 for stable URL.
          const pngName = pdfFile.driveFile.name.replace(/\.pdf$/i, '.png');
          const uploaded = await uploadAsset(pngBlob, siteId, 'sld', pngName);
          useMonitoringStore.getState().addSLD({
            id: `sld_drive_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            name: pdfFile.driveFile.name.replace(/\.pdf$/i, ''),
            imageUrl: uploaded.url,
            dataPointMappings: [],
            driveFileId: pdfFile.driveFile.id,
          });
        } else {
          // Overlay → place at map center, user repositions on top of orthophoto.
          // Use blob: URL for immediate Mapbox preview (Image() with
          // crossOrigin='anonymous' can't auth /api/v2/assets); projectSync
          // uploads the blob on save.
          const map = useMapStore.getState().map;
          const center = map ? map.getCenter() : { lng: useMapStore.getState().center[0], lat: useMapStore.getState().center[1] };
          const wMeters = 250, hMeters = 150, mpdLat = 111320;
          const mpdLng = 111320 * Math.cos(center.lat * Math.PI / 180);
          const dLng = wMeters / mpdLng / 2;
          const dLat = hMeters / mpdLat / 2;
          const bounds: [[number, number], [number, number], [number, number], [number, number]] = [
            [center.lng - dLng, center.lat + dLat],
            [center.lng + dLng, center.lat + dLat],
            [center.lng + dLng, center.lat - dLat],
            [center.lng - dLng, center.lat - dLat],
          ];

          useProjectStore.getState().addOverlay({
            id: `overlay_drive_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
            name: pdfFile.driveFile.name.replace(/\.pdf$/i, ''),
            url: URL.createObjectURL(pngBlob),
            bounds,
            opacity: 0.75,
            visible: true,
          });
        }
      } catch (err) {
        console.error('PDF post-processing error:', err);
      }
    }

    // Register the capture dates of everything just imported so the new dates
    // are added to visibleDates and the markers render on the map immediately
    // (same as the local "Upload Geo Photos" path). Without this the indication
    // count updates but MapCanvas's date filter hides the markers until a
    // save/reload runs updateAvailableDates() via loadProject.
    useProjectStore.getState().updateAvailableDates();

    setStep('summary');
  }, [siteId, files, addIndication, addFlightPath]);

  const handleCancel = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    closeModal();
  }, [closeModal]);

  // ── Render ──

  if (!siteId) {
    return (
      <div className="drive-import-backdrop" onClick={closeModal}>
        <div className="drive-import-modal" onClick={(e) => e.stopPropagation()}>
          <p>Please save the project to a site first.</p>
          <button onClick={closeModal}>Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="drive-import-backdrop" onClick={closeModal}>
      <div className="drive-import-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="drive-import-header">
          <h2>Import from Google Drive</h2>
          <button className="drive-import-close" onClick={handleCancel}>×</button>
        </div>

        {/* Step indicator */}
        <div className="drive-import-steps">
          {(['url', 'review', 'importing', 'summary'] as WizardStep[]).map((s, i) => (
            <span
              key={s}
              className={`drive-import-step ${step === s ? 'active' : ''} ${
                ['url', 'review', 'importing', 'summary'].indexOf(step) > i ? 'done' : ''
              }`}
            >
              {i + 1}. {s === 'url' ? 'Folder' : s === 'review' ? 'Review' : s === 'importing' ? 'Import' : 'Done'}
            </span>
          ))}
        </div>

        {/* Step 1: URL Input */}
        {step === 'url' && (
          <div className="drive-import-body">
            <p className="drive-import-hint">
              Paste the link to a publicly shared Google Drive folder containing your site data (photos, videos, SRT files, PDFs). Subfolders are scanned automatically.
            </p>
            <div className="drive-import-url-row">
              <input
                type="text"
                className="drive-import-input"
                placeholder="https://drive.google.com/drive/folders/..."
                value={folderUrl}
                onChange={(e) => setFolderUrl(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleScan()}
                autoFocus
              />
              <button
                className="drive-import-btn drive-import-btn--primary"
                onClick={handleScan}
                disabled={scanning || !folderUrl.trim()}
              >
                {scanning ? 'Scanning...' : 'Scan Folder'}
              </button>
            </div>
            {scanError && <p className="drive-import-error">{scanError}</p>}
          </div>
        )}

        {/* Step 2: Review */}
        {step === 'review' && currentSummary && (
          <div className="drive-import-body">
            {truncationReason && (
              <div className="drive-import-warning">
                {truncationReason === 'files' && (
                  <>Folder contained more than 2000 files — showing the first 2000. Consider importing subfolders one at a time.</>
                )}
                {truncationReason === 'depth' && (
                  <>Folder tree is deeper than 8 levels — deepest subfolders were skipped.</>
                )}
                {truncationReason === 'folders' && (
                  <>Folder contains more than 200 subfolders — some were not scanned. Consider importing top-level subfolders separately.</>
                )}
              </div>
            )}
            {alreadyFlaggedCount > 0 && (
              <div className="drive-import-warning drive-import-warning--dup">
                <span>
                  {alreadyFlaggedCount} file{alreadyFlaggedCount === 1 ? ' is' : 's are'} already in this
                  project — unchecked to prevent duplicates.
                </span>
                <button className="drive-import-btn drive-import-btn--inline" onClick={includeAlreadyImported}>
                  Include anyway
                </button>
              </div>
            )}
            {/* Summary bar */}
            <div className="drive-import-summary-bar">
              <span>{currentSummary.photoSets.length} photo sets</span>
              <span>{currentSummary.flightPaths.length} flight paths</span>
              <span>{currentSummary.geotiffs.length} orthophotos</span>
              <span>{currentSummary.pdfs.length} PDFs</span>
              <span className="drive-import-summary-size">
                {formatBytes(currentSummary.r2Bytes)} → R2 &nbsp;|&nbsp;
                {formatBytes(currentSummary.driveByes)} stays on Drive
              </span>
            </div>

            {/* File list */}
            <div className="drive-import-file-list">
              {/* Photo Sets */}
              {reviewPhotoSets.length > 0 && (
                <div className="drive-import-group">
                  <h3>Photo Sets ({reviewPhotoSets.length})</h3>
                  {reviewPhotoSets.map((set) => (
                    <PhotoSetRow
                      key={`${set.pathPrefix}${set.baseName}`}
                      set={set}
                      files={files}
                      onToggleSet={toggleFileSet}
                    />
                  ))}
                </div>
              )}

              {/* Flight Paths */}
              {reviewFlightPaths.length > 0 && (
                <div className="drive-import-group">
                  <h3>Flight Paths ({reviewFlightPaths.length})</h3>
                  {reviewFlightPaths.map((set) => (
                    <FlightPathRow
                      key={`${set.pathPrefix}${set.baseName}`}
                      set={set}
                      files={files}
                      onToggle={toggleFile}
                    />
                  ))}
                </div>
              )}

              {/* GeoTIFFs */}
              {reviewGeotiffs.length > 0 && (
                <div className="drive-import-group">
                  <h3>Orthophotos ({reviewGeotiffs.length})</h3>
                  {reviewGeotiffs.map((f) => {
                    const idx = files.indexOf(f);
                    return (
                      <FileRow key={f.driveFile.id} file={f} idx={idx} onToggle={toggleFile} />
                    );
                  })}
                </div>
              )}

              {/* PDFs */}
              {reviewPdfs.length > 0 && (
                <div className="drive-import-group">
                  <h3>PDFs ({reviewPdfs.length})</h3>
                  {reviewPdfs.map((f) => {
                    const idx = files.indexOf(f);
                    return (
                      <PdfRow
                        key={f.driveFile.id}
                        file={f}
                        idx={idx}
                        onToggle={toggleFile}
                        onPurposeChange={setPdfPurpose}
                      />
                    );
                  })}
                </div>
              )}

              {/* Unknown */}
              {reviewUnknowns.length > 0 && (
                <div className="drive-import-group">
                  <h3>Unknown / Skipped ({reviewUnknowns.length})</h3>
                  {reviewUnknowns.map((f) => (
                    <div key={f.driveFile.id} className="drive-import-file-row drive-import-file-row--dimmed">
                      <span className="drive-import-file-name">{f.driveFile.name}</span>
                      <span className="drive-import-file-size">{formatBytes(parseInt(f.driveFile.size, 10))}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="drive-import-actions">
              <button className="drive-import-btn" onClick={() => setStep('url')}>Back</button>
              <button
                className="drive-import-btn drive-import-btn--primary"
                onClick={handleImport}
                disabled={files.filter((f) => f.included).length === 0}
              >
                Import ({filesToProcess(files).length} files to process)
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Importing */}
        {step === 'importing' && progress && (
          <div className="drive-import-body">
            <div className="drive-import-progress-header">
              <span>Importing {progress.completed} / {progress.total}</span>
              {progress.currentFile && (
                <span className="drive-import-current-file">{progress.currentFile}</span>
              )}
            </div>
            <div className="drive-import-progress-bar">
              <div
                className="drive-import-progress-fill"
                style={{ width: `${progress.total > 0 ? (progress.completed / progress.total) * 100 : 0}%` }}
              />
            </div>
            <div className="drive-import-progress-list">
              {progress.files.map((fp) => (
                <div
                  key={fp.file.driveFile.id}
                  className={`drive-import-progress-item drive-import-progress-item--${fp.status}`}
                >
                  <span className="drive-import-progress-status">
                    {fp.status === 'queued' ? '○' : fp.status === 'importing' ? '◌' : fp.status === 'done' ? '●' : '✕'}
                  </span>
                  <span className="drive-import-progress-name">{fp.file.driveFile.name}</span>
                  {fp.error && <span className="drive-import-progress-error">{fp.error}</span>}
                </div>
              ))}
            </div>
            <div className="drive-import-actions">
              <button className="drive-import-btn drive-import-btn--danger" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Summary */}
        {step === 'summary' && progress && (
          <div className="drive-import-body">
            <div className="drive-import-summary-result">
              <div className="drive-import-stat drive-import-stat--success">
                {progress.files.filter((f) => f.status === 'done').length} files imported successfully
              </div>
              {progress.files.some((f) => f.status === 'error') && (
                <div className="drive-import-stat drive-import-stat--error">
                  {progress.files.filter((f) => f.status === 'error').length} files failed
                  <ul>
                    {progress.files
                      .filter((f) => f.status === 'error')
                      .map((f) => (
                        <li key={f.file.driveFile.id}>
                          {f.file.driveFile.name}: {f.error}
                        </li>
                      ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="drive-import-actions">
              <button className="drive-import-btn drive-import-btn--primary" onClick={closeModal}>
                Close &amp; View on Map
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ──

function PhotoSetRow({
  set,
  files,
  onToggleSet,
}: {
  set: PhotoSet;
  files: CategorizedFile[];
  onToggleSet: (idxs: number[]) => void;
}) {
  // Anchor priority mirrors the import path (thermal ?? visual ?? zoom) so
  // visual-only / zoom-only sets are visible and toggleable, not hidden.
  const anchor = set.thermal ?? set.visual ?? set.zoom;
  if (!anchor) return null;
  const members = [set.thermal, set.visual, set.zoom].filter(
    (f): f is CategorizedFile => !!f
  );
  const idxs = members.map((f) => files.indexOf(f)).filter((i) => i >= 0);
  // Only a thermal anchor is uploaded to R2. Visual/zoom photos always stay
  // on Drive — a promoted visual/zoom anchor only gets an EXIF peek.
  const anchorSize = parseInt(anchor.driveFile.size, 10) || 0;
  const siblingSize = members
    .filter((f) => f !== anchor)
    .reduce((n, f) => n + (parseInt(f.driveFile.size, 10) || 0), 0);

  return (
    <div className="drive-import-file-row">
      <input
        type="checkbox"
        checked={anchor.included}
        onChange={() => onToggleSet(idxs)}
      />
      <span className="drive-import-file-name">
        {set.pathPrefix && <span className="drive-import-path-prefix">{set.pathPrefix}</span>}
        {set.baseName}
      </span>
      {set.thermal && <span className="drive-import-badge drive-import-badge--thermal">T</span>}
      {set.visual && <span className="drive-import-badge drive-import-badge--visual">V</span>}
      {set.zoom && <span className="drive-import-badge drive-import-badge--zoom">Z</span>}
      {anchor.alreadyImported && <span className="drive-import-badge drive-import-badge--dup">already imported</span>}
      <span className="drive-import-file-size">
        {set.thermal ? (
          <>
            {formatBytes(anchorSize)} → R2
            {siblingSize > 0 && ` + ${formatBytes(siblingSize)} on Drive`}
          </>
        ) : (
          `${formatBytes(anchorSize + siblingSize)} on Drive`
        )}
      </span>
    </div>
  );
}

function FlightPathRow({
  set,
  files,
  onToggle,
}: {
  set: FlightPathSet;
  files: CategorizedFile[];
  onToggle: (idx: number) => void;
}) {
  const primarySrt = primaryFlightSrt(set);
  if (!primarySrt) return null;
  const idx = files.indexOf(primarySrt);
  const sizeOf = (f?: CategorizedFile) => (f ? parseInt(f.driveFile.size, 10) || 0 : 0);
  const videoBadges: Array<{ role: string; label: string; cls: string; size: number }> = [
    { role: 'thermal', label: 'Thermal', cls: 'drive-import-badge--thermal', size: sizeOf(set.thermalVideo) },
    { role: 'visual', label: 'Visual', cls: 'drive-import-badge--visual', size: sizeOf(set.visualVideo) },
    { role: 'zoom', label: 'Zoom', cls: 'drive-import-badge--zoom', size: sizeOf(set.zoomVideo) },
    { role: 'screen', label: 'Screen (liveview)', cls: 'drive-import-badge--screen', size: sizeOf(set.screenVideo) },
  ].filter((b) =>
    (b.role === 'thermal' && set.thermalVideo) ||
    (b.role === 'visual' && set.visualVideo) ||
    (b.role === 'zoom' && set.zoomVideo) ||
    (b.role === 'screen' && set.screenVideo)
  );
  const totalVideoSize = videoBadges.reduce((n, b) => n + b.size, 0);

  return (
    <div className="drive-import-file-row">
      <input
        type="checkbox"
        checked={primarySrt.included}
        onChange={() => onToggle(idx)}
      />
      <span className="drive-import-file-name">
        {set.pathPrefix && <span className="drive-import-path-prefix">{set.pathPrefix}</span>}
        {set.baseName}
      </span>
      {primarySrt && <span className="drive-import-badge drive-import-badge--srt">SRT</span>}
      {primarySrt.alreadyImported && <span className="drive-import-badge drive-import-badge--dup">already imported</span>}
      {videoBadges.map((b) => (
        <span key={b.role} className={`drive-import-badge ${b.cls}`}>
          {b.label} ({formatBytes(b.size)})
        </span>
      ))}
      {totalVideoSize > 0 && (
        <span className="drive-import-file-size">{formatBytes(totalVideoSize)} on Drive</span>
      )}
    </div>
  );
}

function FileRow({
  file,
  idx,
  onToggle,
}: {
  file: CategorizedFile;
  idx: number;
  onToggle: (idx: number) => void;
}) {
  return (
    <div className="drive-import-file-row">
      <input type="checkbox" checked={file.included} onChange={() => onToggle(idx)} />
      <span className="drive-import-file-name">
        {file.driveFile.path && (
          <span className="drive-import-path-prefix">{file.driveFile.path}</span>
        )}
        {file.driveFile.name}
      </span>
      {file.alreadyImported && <span className="drive-import-badge drive-import-badge--dup">already imported</span>}
      <span className="drive-import-file-size">
        {formatBytes(parseInt(file.driveFile.size, 10))} → R2
      </span>
    </div>
  );
}

function PdfRow({
  file,
  idx,
  onToggle,
  onPurposeChange,
}: {
  file: CategorizedFile;
  idx: number;
  onToggle: (idx: number) => void;
  onPurposeChange: (idx: number, purpose: PdfPurpose) => void;
}) {
  return (
    <div className="drive-import-file-row">
      <input type="checkbox" checked={file.included} onChange={() => onToggle(idx)} />
      <span className="drive-import-file-name">
        {file.driveFile.path && (
          <span className="drive-import-path-prefix">{file.driveFile.path}</span>
        )}
        {file.driveFile.name}
      </span>
      {file.alreadyImported && <span className="drive-import-badge drive-import-badge--dup">already imported</span>}
      <span className="drive-import-file-size">{formatBytes(parseInt(file.driveFile.size, 10))}</span>
      <select
        className="drive-import-pdf-select"
        value={file.pdfPurpose || 'overlay'}
        onChange={(e) => onPurposeChange(idx, e.target.value as PdfPurpose)}
      >
        <option value="overlay">Overlay (Map)</option>
        <option value="sld">SLD Diagram</option>
      </select>
    </div>
  );
}
